import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { NeuralMeshBackground } from '@/components/three/NeuralMeshBackground';
import { tokenomicsConfig, formatNumber } from '@/data/tokenomicsData';
import { ArrowDown, Download, PlayCircle, ChevronRight } from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    
    if (countRef.current) {
      observer.observe(countRef.current);
    }
    
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);
  
  return (
    <span ref={countRef}>
      {prefix}{typeof end === 'string' ? (hasAnimated ? end : '0') : count}{suffix}
    </span>
  );
};

export const HeroSection = () => {
  const scrollToDistribution = () => {
    document.getElementById('distribution')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const scrollToVesting = () => {
    document.getElementById('vesting')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const scrollToData = () => {
    document.getElementById('data')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 3D Background */}
      <NeuralMeshBackground className="opacity-60" />
      
      {/* Grid overlay */}
      <div className="absolute inset-0 neural-grid opacity-50" />
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-sm text-muted-foreground">$NEURO Token Economics</span>
        </motion.div>
        
        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground mb-6"
        >
          <span className="text-primary text-glow">$NEURO</span> Tokenomics
        </motion.h1>
        
        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-12"
        >
          Fair-Launch Oriented. High Float. Deep Liquidity. DAO Ready.
        </motion.p>
        
        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-16"
        >
          <Button 
            size="lg" 
            onClick={scrollToDistribution}
            className="bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 glow-primary"
          >
            Explore Distribution
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            onClick={scrollToVesting}
            className="border-primary/30 hover:bg-primary/10 hover:border-primary/50"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Simulate Vesting
          </Button>
          <Button 
            size="lg" 
            variant="ghost" 
            onClick={scrollToData}
            className="text-muted-foreground hover:text-foreground"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
        </motion.div>
        
        {/* Stat Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
        >
          {tokenomicsConfig.keyMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="glass rounded-xl p-6 border-glow animate-pulse-glow"
              style={{ animationDelay: `${index * 0.5}s` }}
            >
              <p className="text-xs text-muted-foreground mb-2">{metric.label}</p>
              <p className="text-2xl sm:text-3xl font-bold text-primary">
                {metric.value}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{metric.subtext}</p>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <button
            onClick={scrollToDistribution}
            className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <span className="text-xs">Scroll to explore</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <ArrowDown className="w-5 h-5" />
            </motion.div>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
